import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Wheat, LogOut, ChevronRight, ChevronLeft, Package, FileText,
  CalendarDays, Search, X, AlertTriangle,
} from "lucide-react";

const MONTHS_RO = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];

function formatRoDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("ro-RO", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

function fmt(n) {
  if (n === null || n === undefined || isNaN(n)) return "0";
  return (Math.round(n * 10000) / 10000).toLocaleString("ro-RO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
}

export default function ViewerPage() {
  const { authAxios, logout, username } = useAuth();
  const [tab, setTab] = useState("rapoarte"); // 'rapoarte' | 'inventare'
  const [reports, setReports] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openReport, setOpenReport] = useState(null);
  const [openInventory, setOpenInventory] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [rRes, iRes] = await Promise.all([
          authAxios.get("/reports"),
          authAxios.get("/inventory"),
        ]);
        setReports(rRes.data);
        setInventories(iRes.data);
      } catch (err) {
        toast.error("Eroare la încărcare");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Group reports by month
  const reportsByMonth = useMemo(() => {
    const groups = {};
    for (const r of reports) {
      const key = r.date.substring(0, 7); // YYYY-MM
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    }
    return Object.entries(groups)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([key, items]) => {
        const [y, m] = key.split("-");
        return {
          key,
          label: `${MONTHS_RO[parseInt(m, 10) - 1]} ${y}`,
          items: items.sort((a, b) => b.date.localeCompare(a.date)),
        };
      });
  }, [reports]);

  const filteredReports = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return reportsByMonth;
    return reportsByMonth
      .map((g) => ({ ...g, items: g.items.filter((r) => r.date.includes(s)) }))
      .filter((g) => g.items.length > 0);
  }, [reportsByMonth, search]);

  const inventoriesSorted = useMemo(() => {
    return [...inventories].sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
  }, [inventories]);

  return (
    <div className="min-h-screen bg-neutral-50 pb-safe" data-testid="viewer-page">
      {/* Mobile header */}
      <header className="sticky top-0 z-20 bg-white border-b border-neutral-200">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
              <Wheat className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-neutral-900 leading-tight">Brutăria LaDenis</h1>
              <p className="text-[10px] text-neutral-500 leading-tight">SC AXA OMEGA · {username}</p>
            </div>
          </div>
          <button
            data-testid="viewer-logout"
            onClick={logout}
            className="flex items-center gap-1 text-xs text-neutral-500 hover:text-red-600 p-2"
            aria-label="Deconectare"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex border-t border-neutral-100">
          <button
            data-testid="tab-rapoarte"
            onClick={() => setTab("rapoarte")}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 py-3 text-sm font-semibold transition-colors ${tab === "rapoarte" ? "text-amber-700 border-b-2 border-amber-600" : "text-neutral-500 border-b-2 border-transparent"}`}
          >
            <FileText className="w-4 h-4" />
            Rapoarte zilnice
          </button>
          <button
            data-testid="tab-inventare"
            onClick={() => setTab("inventare")}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 py-3 text-sm font-semibold transition-colors ${tab === "inventare" ? "text-amber-700 border-b-2 border-amber-600" : "text-neutral-500 border-b-2 border-transparent"}`}
          >
            <Package className="w-4 h-4" />
            Inventare lunare
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4">
        {loading && (
          <div className="py-16 text-center text-sm text-neutral-400">Se încarcă...</div>
        )}

        {/* Rapoarte tab */}
        {!loading && tab === "rapoarte" && (
          <div data-testid="rapoarte-tab">
            <div className="relative mb-4">
              <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="reports-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Caută dată (ex: 2026-05)..."
                className="w-full rounded-lg border border-neutral-200 bg-white pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>

            {filteredReports.length === 0 && (
              <div className="text-center py-16">
                <FileText className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
                <p className="text-sm text-neutral-500">Niciun raport zilnic salvat încă.</p>
              </div>
            )}

            {filteredReports.map((group) => (
              <div key={group.key} className="mb-5">
                <h3 className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-2 px-1">
                  {group.label} · {group.items.length} {group.items.length === 1 ? "raport" : "rapoarte"}
                </h3>
                <div className="bg-white rounded-xl border border-neutral-200 shadow-sm divide-y divide-neutral-100 overflow-hidden">
                  {group.items.map((r) => {
                    const totalCons = r.materials.reduce((s, m) => s + (m.consumption || 0), 0);
                    return (
                      <button
                        key={r.id}
                        data-testid={`open-report-${r.date}`}
                        onClick={() => setOpenReport(r)}
                        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-amber-50/40 transition-colors"
                      >
                        <div className="w-10 h-10 rounded-lg bg-amber-50 flex flex-col items-center justify-center shrink-0">
                          <span className="text-[9px] text-amber-700 font-bold uppercase">{MONTHS_RO[parseInt(r.date.substring(5, 7), 10) - 1].substring(0, 3)}</span>
                          <span className="text-sm text-amber-900 font-bold leading-none">{r.date.substring(8, 10)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-neutral-900">{formatRoDate(r.date)}</div>
                          <div className="text-xs text-neutral-500 mt-0.5">
                            {r.products.length} produse · {r.materials.length} materii · {fmt(totalCons)} kg consum
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-neutral-300 shrink-0" />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Inventare tab */}
        {!loading && tab === "inventare" && (
          <div data-testid="inventare-tab">
            {inventoriesSorted.length === 0 && (
              <div className="text-center py-16">
                <Package className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
                <p className="text-sm text-neutral-500">Niciun inventar lunar salvat încă.</p>
              </div>
            )}
            <div className="bg-white rounded-xl border border-neutral-200 shadow-sm divide-y divide-neutral-100 overflow-hidden">
              {inventoriesSorted.map((inv) => {
                const totalStock = inv.materials.reduce((s, m) => s + (m.initial_stock || 0), 0);
                const nonZero = inv.materials.filter((m) => m.initial_stock > 0).length;
                return (
                  <button
                    key={inv.id}
                    data-testid={`open-inventory-${inv.year}-${inv.month}`}
                    onClick={() => setOpenInventory(inv)}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-emerald-50/40 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                      <CalendarDays className="w-5 h-5 text-emerald-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-neutral-900">{MONTHS_RO[inv.month - 1]} {inv.year}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">
                        {nonZero} materii cu stoc · total {fmt(totalStock)} kg
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-neutral-300 shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* Report detail sheet */}
      {openReport && (
        <DetailSheet
          title={formatRoDate(openReport.date)}
          subtitle={`Raport zilnic · ${openReport.date}`}
          onClose={() => setOpenReport(null)}
        >
          <ReportDetail report={openReport} />
        </DetailSheet>
      )}

      {/* Inventory detail sheet */}
      {openInventory && (
        <DetailSheet
          title={`${MONTHS_RO[openInventory.month - 1]} ${openInventory.year}`}
          subtitle="Inventar lunar"
          onClose={() => setOpenInventory(null)}
        >
          <InventoryDetail inv={openInventory} />
        </DetailSheet>
      )}
    </div>
  );
}

function DetailSheet({ title, subtitle, onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-neutral-900/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="detail-sheet"
    >
      <div
        className="absolute inset-x-0 bottom-0 top-12 bg-white rounded-t-2xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
          <button
            data-testid="back-from-detail"
            onClick={onClose}
            className="inline-flex items-center gap-1 text-xs text-neutral-600 hover:text-amber-700 -ml-2 p-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Înapoi
          </button>
          <div className="text-right">
            <h2 className="text-sm font-bold text-neutral-900 leading-tight">{title}</h2>
            <p className="text-[10px] text-neutral-500 leading-tight">{subtitle}</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto thin-scroll">
          {children}
        </div>
      </div>
    </div>
  );
}

function ReportDetail({ report }) {
  const totalCons = report.materials.reduce((s, m) => s + (m.consumption || 0), 0);
  const totalEntries = report.materials.reduce((s, m) => s + (m.entries || 0), 0);
  return (
    <div className="p-4 space-y-5">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">Produse</div>
          <div className="text-xl font-bold text-amber-900 tabular-nums">{report.products.length}</div>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">Materii</div>
          <div className="text-xl font-bold text-emerald-900 tabular-nums">{report.materials.length}</div>
        </div>
        <div className="bg-neutral-100 rounded-lg p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-neutral-600 font-semibold">Consum</div>
          <div className="text-xl font-bold text-neutral-900 tabular-nums">{fmt(totalCons)}<span className="text-[10px] font-normal text-neutral-500"> kg</span></div>
        </div>
      </div>

      {/* Products */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-700 mb-2 px-1">
          Produse fabricate
        </h3>
        <div className="bg-white rounded-lg border border-neutral-200 divide-y divide-neutral-100">
          {report.products.map((p, idx) => (
            <div key={idx} className="px-3 py-2 flex items-center justify-between">
              <span className="text-sm text-neutral-800 truncate flex-1 pr-2">{p.name}</span>
              <span className="text-sm font-bold text-amber-700 tabular-nums">
                {fmt(p.quantity)} <span className="text-[10px] text-neutral-400 font-normal">{p.unit}</span>
              </span>
            </div>
          ))}
          {report.products.length === 0 && (
            <div className="px-3 py-4 text-center text-xs text-neutral-400">Niciun produs</div>
          )}
        </div>
      </div>

      {/* Materials with stocks */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-700 mb-2 px-1">
          Stoc & consum materii prime
        </h3>
        <div className="text-[10px] text-neutral-500 mb-2 px-1">
          Total intrări: <strong className="text-emerald-700">{fmt(totalEntries)} kg</strong>
        </div>
        <div className="bg-white rounded-lg border border-neutral-200 divide-y divide-neutral-100">
          {report.materials.map((m, idx) => {
            const negStock = m.final_stock < 0;
            const lowStock = !negStock && m.final_stock < 5;
            return (
              <div key={idx} className="px-3 py-2.5">
                <div className="flex items-center gap-1.5 mb-1">
                  {(negStock || lowStock) && (
                    <AlertTriangle className={`w-3 h-3 ${negStock ? "text-red-600" : "text-amber-600"}`} />
                  )}
                  <span className="text-sm font-semibold text-neutral-900 capitalize">{m.name}</span>
                </div>
                <div className="grid grid-cols-4 gap-1 text-[10px]">
                  <div>
                    <div className="text-neutral-400 uppercase">Iniț</div>
                    <div className="text-neutral-800 font-semibold tabular-nums">{fmt(m.initial_stock)}</div>
                  </div>
                  <div>
                    <div className="text-neutral-400 uppercase">Intr</div>
                    <div className="text-emerald-700 font-semibold tabular-nums">{fmt(m.entries)}</div>
                  </div>
                  <div>
                    <div className="text-neutral-400 uppercase">Cons</div>
                    <div className="text-amber-700 font-semibold tabular-nums">{fmt(m.consumption)}</div>
                  </div>
                  <div>
                    <div className="text-neutral-400 uppercase">Final</div>
                    <div className={`font-bold tabular-nums ${negStock ? "text-red-600" : lowStock ? "text-amber-700" : "text-emerald-700"}`}>{fmt(m.final_stock)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function InventoryDetail({ inv }) {
  const totalStock = inv.materials.reduce((s, m) => s + (m.initial_stock || 0), 0);
  const filled = inv.materials.filter((m) => m.initial_stock > 0);
  const empty = inv.materials.filter((m) => !m.initial_stock || m.initial_stock <= 0);
  return (
    <div className="p-4 space-y-5">
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-emerald-50 rounded-lg p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">Materii cu stoc</div>
          <div className="text-xl font-bold text-emerald-900 tabular-nums">{filled.length}<span className="text-[11px] text-emerald-700 font-normal"> / {inv.materials.length}</span></div>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">Total stoc</div>
          <div className="text-xl font-bold text-amber-900 tabular-nums">{fmt(totalStock)}<span className="text-[10px] font-normal text-amber-700"> kg</span></div>
        </div>
      </div>

      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-700 mb-2 px-1">
          Materii prime cu stoc inițial
        </h3>
        {filled.length === 0 && (
          <div className="text-center py-6 text-xs text-neutral-400 bg-white rounded-lg border border-neutral-200">
            Nicio materie primă cu stoc inițial.
          </div>
        )}
        {filled.length > 0 && (
          <div className="bg-white rounded-lg border border-neutral-200 divide-y divide-neutral-100">
            {filled.map((m, idx) => (
              <div key={idx} className="px-3 py-2 flex items-center justify-between">
                <span className="text-sm text-neutral-800 capitalize truncate flex-1 pr-2">{m.name}</span>
                <span className="text-sm font-bold text-emerald-700 tabular-nums">
                  {fmt(m.initial_stock)} <span className="text-[10px] text-neutral-400 font-normal">kg</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {empty.length > 0 && (
        <details className="bg-neutral-50 rounded-lg border border-neutral-200">
          <summary className="px-3 py-2 text-xs font-semibold text-neutral-600 cursor-pointer hover:text-neutral-900">
            Materii prime fără stoc ({empty.length})
          </summary>
          <div className="px-3 py-2 text-[11px] text-neutral-500 capitalize leading-relaxed">
            {empty.map((m) => m.name).join(", ")}
          </div>
        </details>
      )}
    </div>
  );
}
